import { ALLOWED_CONTACT_REQUEST_TYPES, Category } from 'models/companies'
import type { SearchType } from './useMobileSearchAPI'
import type { MobileSearchFilters } from './useMobileSearch'
import { ALL_SEARCH_TYPE_OPTIONS } from './searchTypeConstants'

// ALLOWED_CONTACT_REQUEST_TYPES is the set of sub-categories that have First
// Bite data (FB data → chains → contact requests), so it doubles as the
// "FB companies/locations exist for this type" check here.
const FB_DATA_SLUGS = new Set<string>(ALLOWED_CONTACT_REQUEST_TYPES)

export const isFbSearchType = (type: SearchType): boolean =>
  type === 'fb_company' || type === 'fb_location'

const CUSTOM_OPTIONS = ALL_SEARCH_TYPE_OPTIONS.filter(
  (opt) => !isFbSearchType(opt.value)
)

const splitSlugs = (value?: string): string[] =>
  value
    ?.split(',')
    .map((slug) => slug.trim())
    .filter(Boolean) ?? []

/**
 * Whether the active opp-category/type filter can contain First Bite data.
 * Custom-only types (e.g. grocery stores) have no FB companies/locations, so
 * FB search types should be hidden — keeping mobile search consistent with
 * desktop, where the FB tabs are simply empty for those types.
 *
 * The `type` filter (sub-categories) is the most specific signal; otherwise we
 * expand the selected categories to their sub-categories. With no filter at
 * all, everything is searchable.
 */
export function filtersHaveFbData(
  categories: Category[] | undefined,
  filters: MobileSearchFilters
): boolean {
  const typeSlugs = splitSlugs(filters.type)
  if (typeSlugs.length > 0) {
    return typeSlugs.some((slug) => FB_DATA_SLUGS.has(slug))
  }

  const categorySlugs = splitSlugs(filters.category)
  if (categorySlugs.length > 0) {
    // Don't restrict until the category → sub-category map has loaded.
    if (!categories) return true
    return categorySlugs.some((catSlug) => {
      const category = categories.find((cat) => cat.slug === catSlug)
      return (
        category?.sub_categories.some((sub) => FB_DATA_SLUGS.has(sub.slug)) ??
        false
      )
    })
  }

  return true
}

/**
 * Search-type options for the mobile search dropdown, narrowed to Custom-only
 * when the active filter has no First Bite data.
 */
export function getMobileSearchTypeOptions(
  categories: Category[] | undefined,
  filters: MobileSearchFilters
): { value: SearchType; label: string }[] {
  return filtersHaveFbData(categories, filters)
    ? ALL_SEARCH_TYPE_OPTIONS
    : CUSTOM_OPTIONS
}
